/**
 * Top Sales Services Table Component
 *
 * A searchable table showing the top performing grooming services with ranking,
 * name, sales count, and total revenue. Uses V3 Table components for consistency.
 */

import React, { useState } from 'react';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import styles from './TopServiceDurationTable.module.scss';
import { ServiceDuration } from '../../Charts/Staff/staffChartsMockData';

interface TopServiceDurationTableProps {
    title: string;
    data: ServiceDuration[];
}

export const TopServiceDurationTable: React.FC<TopServiceDurationTableProps> = ({ title, data }) => {
    const [searchText, setSearchText] = useState('');

    // Filter data based on search text
    const filteredData = data.filter(service => service.name.toLowerCase().includes(searchText.toLowerCase()));

    return (
        <div className={styles.tableContainer}>
            {/* Table Header */}
            <div className={styles.tableHeader}>
                <div className={styles.headerContent}>
                    <h3 className={styles.tableTitle}>{title}</h3>
                </div>
            </div>

            {/* Search Bar */}
            <div className={styles.searchSection}>
                <SearchBar
                    searchText={searchText}
                    placeholder="Filter by service"
                    onChangeSearchText={setSearchText}
                    className={styles.searchBar}
                />
            </div>

            {/* Data Table */}
            <div className={styles.tableWrapper}>
                <Table className={styles.table}>
                    <TableHeader>
                        <TableHeaderCell className={styles.rankingColumn}>Ranking</TableHeaderCell>
                        <TableHeaderCell className={styles.nameColumn}>Name</TableHeaderCell>
                        <TableHeaderCell className={styles.durationColumn}>Avg duration</TableHeaderCell>
                        <TableHeaderCell className={styles.sessionsColumn}>Total sessions</TableHeaderCell>
                    </TableHeader>

                    <TableBody>
                        {filteredData.map(service => (
                            <TableRow key={service.ranking}>
                                <TableCell className={styles.rankingCell}>{service.ranking}</TableCell>
                                <TableCell className={styles.nameCell}>{service.name}</TableCell>
                                <TableCell className={styles.durationCell}>{service.duration}</TableCell>
                                <TableCell className={styles.sessionsCell}>{service.sessions}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* No results message */}
                {filteredData.length === 0 && (
                    <div className={styles.noResults}>
                        <p>No services found matching &rdquo;{searchText}&rdquo;</p>
                    </div>
                )}
            </div>
        </div>
    );
};
